import React, {useMemo, useState} from "react";
import {Backdrop, Box, Button, Chip, CircularProgress, Tooltip, Typography} from "@mui/material";
import {DataGridPro} from "@mui/x-data-grid-pro";
import {orange} from "@mui/material/colors";
import {useTranslation} from "react-i18next";

import ServiceTypeIcons from "../common/ServiceTypeIcons";
import NoRowsOverlayLabel from "../common/NoRowsOverlayLabel";
import {formatMinutesAsHours} from "../../helpers/DateTimeRangeHelper";
import {
    SHIFT_ADMIN_VIEWS
} from "../../constants/ShiftAdminConstants";
import PerformerShiftSummaryDialog from "./PerformerShiftSummaryDialog";
import ShiftAdminPerformerAmendmentsDialog from "./ShiftAdminPerformerAmendmentsDialog";
import ShiftAdminAddAmendmentDialog from "./ShiftAdminAddAmendmentDialog";
import ShiftCountChip from "./ShiftCountChip";
import {useTimezone} from "../../hooks/useTimezone";

const SINGLE_LINE_ROW_HEIGHT_PIXELS = 40;

const styles = {
    tableCard: {
        border: "0.06rem solid",
        borderColor: "divider",
        borderRadius: 1
    },
    dataGrid: {
        maxHeight: "calc(100vh - 13.25rem)",
        border: 0,
        "& .MuiDataGrid-cell": {
            backgroundColor: "var(--mui-common-white)",
            display: "flex",
            alignItems: "center",
            fontSize: "0.875rem",
            lineHeight: "1.125rem !important",
            whiteSpace: "normal",
            "& .MuiTypography-root": {
                fontSize: "0.875rem"
            },
            "&:focus": {
                outline: "none"
            },
            "&:focus-within": {
                outline: "none"
            }
        },
        "& .MuiDataGrid-columnHeader": {
            backgroundColor: "var(--mui-common-white)",
            fontSize: "0.875rem"
        },
        "& .MuiDataGrid-row:hover": {
            backgroundColor: "var(--mui-common-white)"
        },
        "&&& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: "var(--mui-common-white)"
        },
        "&&& .MuiDataGrid-row:focus": {
            backgroundColor: "var(--mui-common-white)",
            outline: "none"
        }
    },
    amendmentPill: {
        backgroundColor: orange[50],
        color: orange[900],
        fontWeight: 500,
        borderRadius: "0.8rem",
        border: `0.063rem solid ${orange[200]}`,
        "&:hover": {
            backgroundColor: orange[100],
            borderColor: orange[300]
        }
    }
};

export default function ShiftAdminPerformerGrid({rows, view, currentDate, isPerformersDataLoading = false, onAmendmentCreated}) {
    const {t} = useTranslation();
    const {timezone} = useTimezone();
    const [openPerformerShiftSummaryDialog, setOpenPerformerShiftSummaryDialog] = useState(false);
    const [selectedPerformerId, setSelectedPerformerId] = useState(null);
    const [openPerformerAmendmentsDialog, setOpenPerformerAmendmentsDialog] = useState(false);
    const [openAddAmendmentDialog, setOpenAddAmendmentDialog] = useState(false);

    const isMonthView = view === SHIFT_ADMIN_VIEWS.MONTH;

    const gridRows = useMemo(() => {
        return rows.map(row => ({
            id: row.performerId,
            ...row
        }));
    }, [rows]);

    const selectedPerformer = useMemo(() => {
        if (selectedPerformerId === null) {
            return null;
        }

        return gridRows.find((row) => row.id === selectedPerformerId) || null;
    }, [gridRows, selectedPerformerId]);

    const handleOpenPerformerShiftSummaryDialog = (performer) => {
        if (!isMonthView) {
            return;
        }

        setOpenPerformerShiftSummaryDialog(true);
        setSelectedPerformerId(performer.id);
    };

    const handleClosePerformerShiftSummaryDialog = () => {
        setOpenPerformerShiftSummaryDialog(false);
        setSelectedPerformerId(null);
    };

    const handleOpenPerformerAmendmentsDialog = (performer) => {
        setOpenPerformerAmendmentsDialog(true);
        setSelectedPerformerId(performer.id);
    };

    const handleOpenAddAmendmentDialog = (performer) => {
        if (isMonthView) {
            return;
        }

        setOpenAddAmendmentDialog(true);
        setSelectedPerformerId(performer.id);
    };

    const handleClosePerformerAmendmentsDialog = () => {
        setOpenPerformerAmendmentsDialog(false);
        setSelectedPerformerId(null)
    };

    const handleCloseAddAmendmentDialog = () => {
        setOpenAddAmendmentDialog(false);
        setSelectedPerformerId(null)
    };

    const columns = useMemo(() => {
        const commonColumns = [
            {
                field: "fullName",
                headerName: t("shiftAdmin.grid.performerColumn"),
                minWidth: 250,
                sortable: false,
                renderCell: (params) => (
                    <Typography>
                        {params.value}
                    </Typography>
                )
            },
            {
                field: "services",
                headerName: t("shiftAdmin.grid.serviceColumn"),
                minWidth: 160,
                sortable: false,
                renderCell: (params) => <ServiceTypeIcons serviceKeycodes={params.value}/>
            },
            {
                field: "totalShiftCount",
                headerName: t("shiftAdmin.grid.shiftsColumn"),
                minWidth: 120,
                renderCell: (params) => {
                    if (isMonthView) {
                        return (
                            <ShiftCountChip
                                count={params.value}
                                onClick={() => handleOpenPerformerShiftSummaryDialog(params.row)}/>
                        );
                    }

                    const shiftTimeRanges = params.row.shiftTimeRanges || [];

                    if (shiftTimeRanges.length === 0) {
                        return params.value;
                    }

                    return (
                        <Tooltip arrow placement="right" title={
                            <Box>
                                {shiftTimeRanges.map((shiftTimeRange, index) => (
                                    <Typography key={`${shiftTimeRange}-${index}`} variant="body2">
                                        {shiftTimeRange}
                                    </Typography>
                                ))}
                            </Box>
                        }>
                            <Typography variant="body2">
                                {params.value}
                            </Typography>
                        </Tooltip>
                    );
                }
            },
            {
                field: "totalMinutesIncludingAmendments",
                headerName: t("shiftAdmin.grid.totalHoursColumn"),
                renderHeader: () => (
                    <Tooltip title={t("common.shiftTableColumns.totalHoursTooltip")}>
                        {t("common.shiftTableColumns.totalHours")}
                    </Tooltip>
                ),
                minWidth: 160,
                sortable: false,
                renderCell: (params) => (
                    <Typography variant="body2">
                        {formatMinutesAsHours(params.value)}
                    </Typography>
                )
            },
            {
                field: "amendmentCount",
                headerName: t("shiftAdmin.grid.amendmentsColumn"),
                minWidth: 165,
                sortable: false,
                valueGetter: (_, row) => isMonthView ? row.totalAmendmentCount : row.amendmentCount,
                renderCell: (params) => (
                    params.value > 0
                    &&
                    <Chip onClick={() => handleOpenPerformerAmendmentsDialog(params.row)}
                        label={t("common.amendmentCountLabel", {count: params.value})}
                        sx={styles.amendmentPill}/>
                )
            }
        ];

        if (!isMonthView) {
            commonColumns.push({
                field: "actions",
                headerName: t("shiftAdmin.grid.actionColumn"),
                minWidth: 200,
                sortable: false,
                renderCell: (params) => (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleOpenAddAmendmentDialog(params.row)}>
                        + {t("shiftAdminAddAmendmentDialog.addAmendmentLabel")}
                    </Button>
                )
            });
        }

        return commonColumns;
    }, [isMonthView, t]);

    const noRowsMessage = isMonthView
        ? t("shiftAdmin.grid.noMonthRows")
        : t("shiftAdmin.grid.noDayRows");

    return (
        <Box sx={styles.tableCard}>
            {
                isPerformersDataLoading
                &&
                <Backdrop sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1}} open={isPerformersDataLoading}>
                    <CircularProgress color="inherit"/>
                </Backdrop>
            }
            <DataGridPro
                disableRowSelectionOnClick
                rowSelection={false}
                disableColumnMenu
                pagination
                initialState={{
                    pagination: {
                        paginationModel: {pageSize: 50}
                    }
                }}
                pageSizeOptions={[50]}
                loading={isPerformersDataLoading}
                rows={gridRows}
                columns={columns}
                getRowHeight={() => SINGLE_LINE_ROW_HEIGHT_PIXELS}
                sx={{
                    ...styles.dataGrid,
                    "& .MuiDataGrid-footerContainer": {
                        minHeight: SINGLE_LINE_ROW_HEIGHT_PIXELS,
                        maxHeight: SINGLE_LINE_ROW_HEIGHT_PIXELS
                    }
                }}
                slots={{
                    noRowsOverlay: () => <NoRowsOverlayLabel message={noRowsMessage} />
                }}
            />
            <PerformerShiftSummaryDialog
                open={openPerformerShiftSummaryDialog}
                onClose={handleClosePerformerShiftSummaryDialog}
                performer={selectedPerformer}
                currentDate={currentDate}/>
            <ShiftAdminPerformerAmendmentsDialog
                open={openPerformerAmendmentsDialog}
                onClose={handleClosePerformerAmendmentsDialog}
                performer={selectedPerformer}
                currentDate={currentDate}
                onAmendmentCreated={onAmendmentCreated}
                activeView={view}/>
            <ShiftAdminAddAmendmentDialog
                open={openAddAmendmentDialog}
                onClose={handleCloseAddAmendmentDialog}
                performer={selectedPerformer}
                timezone={timezone}
                onAmendmentCreated={onAmendmentCreated}/>
        </Box>
    );
}
